import { CssBaseline, ThemeProvider } from "@mui/material";
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import Loading from "./components/Loading";
import { AlertProvider } from "./contexts/AlertContext";
import { ThemeModeProvider, useThemeMode } from "./contexts/ThemeContext";
import i18n from "./i18n/config";
import { PrimaryAppLayout, SecondMiniAppRoutes } from "./mini-apps";
import SettingPage from "./pages/setting/Page";
import { createAppTheme } from "./theme/theme";
import { getTelegramLaunchData, isSecondMiniAppParam } from "./hooks/useTelegramLaunchInfo";

const QrCodeLockPage = lazy(() => import("./pages/qr-code-lock/Page"));

// Lazy load all pages for code splitting
const HomePage = lazy(() => import("./pages/HomePage"));
const DashboardPage = lazy(() => import("./pages/dashboard/Page"));
const ContributionForm = lazy(
  () => import("./pages/guest-contributions/ContributionForm"),
);
const GuestDetailPage = lazy(
  () => import("./pages/guest-contributions/Detail"),
);
const GuestContributionSearchPage = lazy(
  () => import("./pages/guest-contributions/Filter"),
);
const GuestContributionPage = lazy(
  () => import("./pages/guest-contributions/Page"),
);
const RSVPFilter = lazy(() => import("./pages/rsvp/Filter"));
const RSVPDetail = lazy(() => import("./pages/rsvp/RSVPSummary"));
const RSVPPage = lazy(() => import("./pages/rsvp/RSVP"));
const ManageGuestAddPage = lazy(
  () => import("./pages/manage-guest/AddGuestPage"),
);
const BulkPrintPage = lazy(() => import("./pages/manage-guest/BulkPrintPage"));
const GuestSearchPage = lazy(() => import("./pages/manage-guest/Filter"));
const ManageGuestPage = lazy(() => import("./pages/manage-guest/Page"));
const ManageTagsPage = lazy(
  () => import("./pages/manage-guest/ManageTagsPage"),
);
const PrintQRCodePage = lazy(() => import("./pages/manage-guest/PrintQRCode"));
const ReportPage = lazy(() => import("./pages/report/Page"));
const ReportGuestPage = lazy(() => import("./pages/report/ReportGuestPage"));
const ReportGuestDetailPage = lazy(
  () => import("./pages/report/ReportGuestDetailPage"),
);
const ReportRsvpPage = lazy(() => import("./pages/report/ReportRsvpPage"));
const ReportContributionPage = lazy(
  () => import("./pages/report/ReportContributionPage"),
);
const UserProfilePage = lazy(() => import("./pages/user-profile/Page"));
const CurrencyPage = lazy(() => import("./pages/setting/currency/Page"));
const ExchangeRatePage = lazy(
  () => import("./pages/setting/exchange-rate/Page"),
);
const GuestInviteMessagePage = lazy(
  () => import("./pages/setting/guest-invite/Page"),
);
const InvitationTemplatePage = lazy(
  () => import("./pages/setting/invitation-template/Page"),
);
const LinkPreviewTitlePage = lazy(
  () => import("./pages/setting/link-preview/Page"),
);
const ThankYouMessagePage = lazy(
  () => import("./pages/setting/thank-you/Page"),
);
const ContactUsPage = lazy(() => import("./pages/contact-us/Page"));
const UserGuidePage = lazy(() => import("./pages/user-guide/Page"));

function SaleSupportRedirect() {
  const location = useLocation();
  return <Navigate to={{
    pathname: location.pathname.replace(/^\/(sell-support|app2)/, "/sale-support"),
    search: location.search,
    hash: location.hash,
  }} replace />;
}

function GroupEntryBoundary({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const saleSupportRoute = /^\/(sale-support|sell-support|app2)(?:\/|$)/.test(location.pathname);
  const [pending, setPending] = useState(() => saleSupportRoute && Boolean(getTelegramLaunchData().startParam?.startsWith("gid_")));
  useEffect(() => {
    if (!saleSupportRoute) setPending(false);
  }, [saleSupportRoute]);
  if (pending && saleSupportRoute) {
    return <Navigate to={{ pathname: "/", search: location.search, hash: location.hash }} replace />;
  }
  return children;
}

function MiniAppRouteDispatcher() {
  const location = useLocation();
  const navigate = useNavigate();
  const groupEntryHandled = useRef(false);

  useEffect(() => {
    const launch = getTelegramLaunchData();
    const isSecondParam = isSecondMiniAppParam(launch.startParam);
    const isPrimaryParam = Boolean(launch.startParam?.startsWith("gid_"));

    if (isPrimaryParam && !groupEntryHandled.current) {
      groupEntryHandled.current = true;
      sessionStorage.setItem("active_mini_app", "primary");
      if (/^\/(sale-support|sell-support|app2)(?:\/|$)/.test(location.pathname)) {
        navigate("/" + location.search + location.hash, { replace: true });
        return;
      }
    }

    if (isSecondParam) {
      sessionStorage.setItem("active_mini_app", "sale-support");
      if (!location.pathname.startsWith("/sale-support")) {
        const target =
          ["sale", "sales", "task", "tasks"].includes(launch.startParam?.toLowerCase() || "")
            ? "/sale-support/sales"
            : "/sale-support";
        navigate(target + location.search + location.hash, { replace: true });
        return;
      }
    }

    if (location.pathname.startsWith("/sell-support")) {
      sessionStorage.setItem("active_mini_app", "sale-support");
      navigate(location.pathname.replace(/^\/sell-support/, "/sale-support") + location.search + location.hash, { replace: true });
      return;
    }

    if (location.pathname.startsWith("/sale-support") || location.pathname.startsWith("/app2")) {
      sessionStorage.setItem("active_mini_app", "sale-support");
      return;
    }

    if (isPrimaryParam || (location.pathname === "/" && !isSecondParam)) {
      sessionStorage.setItem("active_mini_app", "primary");
      return;
    }

    sessionStorage.setItem("active_mini_app", "primary");
  }, [location.pathname, location.search, location.hash, navigate]);

  return null;
}

function AppContent() {
  const { mode } = useThemeMode();
  const [language, setLanguage] = useState(i18n.language || "en");

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = language;
      document.documentElement.setAttribute("data-theme", mode);
    }
    const handleLanguageChange = (lng: string) => {
      setLanguage(lng);
      if (typeof document !== "undefined") {
        document.documentElement.lang = lng;
      }
    };
    i18n.on("languageChanged", handleLanguageChange);
    return () => {
      i18n.off("languageChanged", handleLanguageChange);
    };
  }, [language, mode]);

  const theme = useMemo(() => createAppTheme(mode, language), [mode, language]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      <AlertProvider>
        <BrowserRouter>
          <MiniAppRouteDispatcher />
          <GroupEntryBoundary>
          <Suspense fallback={<Loading open />}>
            <Routes>
              {/* ============================================================ */}
              {/* Sale Support Mini App (URL: /sale-support/*)                  */}
              {/* ============================================================ */}
              <Route path="/sale-support/*" element={<SecondMiniAppRoutes />} />
              <Route path="/sell-support/*" element={<SaleSupportRedirect />} />
              <Route path="/app2/*" element={<SaleSupportRedirect />} />

              {/* ============================================================ */}
              {/* Primary Mini App (E-Theap Invitation & Management)           */}
              {/* Preserves 100% existing authentication, state, and paths     */}
              {/* ============================================================ */}
              <Route element={<PrimaryAppLayout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/auth" element={<Navigate to="/" replace />} />
                <Route
                  path="/guest-contributions"
                  element={<GuestContributionPage />}
                />
                <Route
                  path="/guest-contributions/search"
                  element={<GuestContributionSearchPage />}
                />
                <Route
                  path="/guest-contributions/add"
                  element={<ContributionForm />}
                />
                <Route
                  path="/guest-contributions/edit/:id"
                  element={<ContributionForm />}
                />
                <Route
                  path="/guest-contributions/detail/:id"
                  element={<GuestDetailPage />}
                />
                <Route path="/manage-guest" element={<ManageGuestPage />} />
                <Route path="/user-profile" element={<UserProfilePage />} />
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/report" element={<ReportPage />} />
                <Route path="/report/guest" element={<ReportGuestPage />} />
                <Route
                  path="/report/guest/detail/:id"
                  element={<ReportGuestDetailPage />}
                />
                <Route path="/report/rsvp" element={<ReportRsvpPage />} />
                <Route
                  path="/report/contribution"
                  element={<ReportContributionPage />}
                />
                <Route
                  path="/manage-guest/search"
                  element={<GuestSearchPage />}
                />
                <Route
                  path="/manage-guest/print/:id"
                  element={<PrintQRCodePage />}
                />
                <Route
                  path="/manage-guest/print-bulk"
                  element={<BulkPrintPage />}
                />
                <Route
                  path="/manage-guest/add"
                  element={<ManageGuestAddPage />}
                />
                <Route
                  path="/manage-guest/edit/:id"
                  element={<ManageGuestAddPage />}
                />
                <Route
                  path="/manage-guest/tags"
                  element={<ManageTagsPage />}
                />
                <Route path="/rsvp" element={<RSVPPage />} />
                <Route path="/rsvp/search" element={<RSVPFilter />} />
                <Route path="/rsvp/:id" element={<RSVPDetail />} />
                <Route path="/setting" element={<SettingPage />} />
                <Route path="/qr-code-lock" element={<QrCodeLockPage />} />
                <Route
                  path="/setting/currency"
                  element={<CurrencyPage />}
                />
                <Route
                  path="/setting/exchange-rate"
                  element={<ExchangeRatePage />}
                />
                <Route
                  path="/setting/guest-invite"
                  element={<GuestInviteMessagePage />}
                />
                <Route
                  path="/setting/invitation-template"
                  element={<InvitationTemplatePage />}
                />
                <Route
                  path="/setting/invitation-templates"
                  element={<InvitationTemplatePage />}
                />
                <Route
                  path="/setting/messages"
                  element={<GuestInviteMessagePage />}
                />
                <Route
                  path="/setting/link-preview"
                  element={<LinkPreviewTitlePage />}
                />
                <Route
                  path="/setting/seo"
                  element={<LinkPreviewTitlePage />}
                />
                <Route
                  path="/setting/thank-you"
                  element={<ThankYouMessagePage />}
                />
                <Route path="/contact-us" element={<ContactUsPage />} />
                <Route path="/feedback" element={<ContactUsPage />} />
                <Route path="/user-guide" element={<UserGuidePage />} />
              </Route>
              {/* Fallback route with strict mini app isolation */}
              <Route
                path="*"
                element={
                  typeof window !== "undefined" &&
                  (sessionStorage.getItem("active_mini_app") === "sale-support" ||
                   sessionStorage.getItem("active_mini_app") === "sell-support") ? (
                    <Navigate to="/sale-support" replace />
                  ) : (
                    <Navigate to="/" replace />
                  )
                }
              />
            </Routes>
          </Suspense>
          </GroupEntryBoundary>
        </BrowserRouter>
          <svg width="0" height="0">
            <filter
              id="mini-liquid-lens"
              x="-50%"
              y="-50%"
              width="200%"
              height="200%"
            >
              <feImage
                x="0"
                y="0"
                result="normalMap"
                xlinkHref="data:image/svg+xml;utf8,
      <svg xmlns='http://www.w3.org/2000/svg' width='300' height='300'>
        <radialGradient id='invmap' cx='50%' cy='50%' r='75%'>
          <stop offset='0%' stop-color='rgb(128,128,255)'/>
          <stop offset='90%' stop-color='rgb(255,255,255)'/>
        </radialGradient>
        <rect width='100%' height='100%' fill='url(#invmap)'/>
      </svg>"
              />
              <feDisplacementMap
                in="SourceGraphic"
                in2="normalMap"
                scale="-252"
                xChannelSelector="R"
                yChannelSelector="G"
                result="displaced"
              />
              <feMerge>
                <feMergeNode in="displaced" />
              </feMerge>
            </filter>
          </svg>
        </AlertProvider>
    </ThemeProvider>
  );
}

function App() {
  return (
    <ThemeModeProvider>
      <AppContent />
    </ThemeModeProvider>
  );
}

export default App;
